import { useCallback, useEffect, useMemo } from "react";
import { AvailableProvider, FormData, ProviderFormState } from "../types";
import { getProviderCapabilities } from "../utils/providerHelpers";
import {
  applySchemaConstValuesToForm,
  isConstSchemaProperty,
  isMerchantIdConstInSchema,
} from "../utils/schemaConstFields";

interface UseProviderFormEffectsProps {
  state: ProviderFormState;
  merchantInfo?: { categoryCode: string } | null;
  providers: AvailableProvider[];
}

export function useProviderFormEffects({
  state,
  merchantInfo,
  providers,
}: UseProviderFormEffectsProps): void {
  const {
    watch,
    setValue,
    getValues,
    setMcc,
    setSelectedProvider,
    formStateRef,
    selectedProvider,
    clearErrors,
  } = state;

  const watchedProvider = watch("provider");

  const capabilities = useMemo(
    () => getProviderCapabilities(selectedProvider),
    [selectedProvider],
  );

  const updateMccFromMerchantInfo = useCallback(() => {
    if (merchantInfo) {
      const categoryCode = Number(merchantInfo.categoryCode);
      const mccValue =
        categoryCode && categoryCode !== 0 ? categoryCode : undefined;
      setValue("mcc", mccValue);
      setMcc(mccValue);
    }
  }, [merchantInfo, setValue, setMcc]);

  useEffect(() => {
    updateMccFromMerchantInfo();
  }, [updateMccFromMerchantInfo]);

  const initializeProviderField = useCallback(() => {
    const values = watch();
    if (values.provider === undefined || values.provider === null) {
      setValue("provider", "");
    }
  }, [watch, setValue]);

  useEffect(() => {
    initializeProviderField();
  }, [initializeProviderField]);

  const updateSelectedProvider = useCallback(() => {
    if (watchedProvider && providers.length > 0) {
      const provider = providers.find((p) => p.id === watchedProvider);
      if (provider) {
        setSelectedProvider(provider);
      }
    } else {
      setSelectedProvider(null);
    }
  }, [watchedProvider, providers, setSelectedProvider]);

  useEffect(() => {
    updateSelectedProvider();
  }, [updateSelectedProvider]);

  useEffect(() => {
    if (!selectedProvider?.schema) {
      return;
    }
    applySchemaConstValuesToForm(selectedProvider.schema, setValue, getValues);
  }, [selectedProvider?.schema, selectedProvider?.id, setValue, getValues]);

  useEffect(() => {
    if (!capabilities.hasBrands) {
      setValue("brands", [], { shouldValidate: false });
      clearErrors("brands");
    }
  }, [capabilities.hasBrands, setValue, clearErrors]);

  const getCredentialsType = useCallback(
    (provider: AvailableProvider | null): string => {
      if (!provider) return "";
      return (
        provider.credentials?.type ||
        provider?.schema?.properties?.credentials?.properties?.type?.const ||
        ""
      );
    },
    [],
  );

  const shouldClearCredentials = useCallback(
    (
      currentProviderId: string | null,
      previousProviderId: string | null,
    ): boolean => {
      return (
        !!currentProviderId &&
        previousProviderId !== null &&
        currentProviderId !== previousProviderId
      );
    },
    [],
  );

  const clearCredentialsFields = useCallback(() => {
    const currentValues = getValues();
    const currentCredentials = currentValues.credentials || {};
    const credentialProperties =
      selectedProvider?.schema?.properties?.credentials?.properties;

    Object.keys(currentCredentials).forEach((key) => {
      if (key === "type") {
        return;
      }
      if (isConstSchemaProperty(credentialProperties?.[key])) {
        return;
      }
      setValue(`credentials.${key}` as keyof FormData, "", {
        shouldValidate: false,
      });
    });
  }, [getValues, setValue, selectedProvider?.schema]);

  const resetCredentialsObject = useCallback(
    (provider: AvailableProvider | null) => {
      const credentialsType = getCredentialsType(provider);
      setValue(
        "credentials",
        { type: credentialsType },
        { shouldValidate: false },
      );
    },
    [getCredentialsType, setValue],
  );

  useEffect(() => {
    if (formStateRef.current.isInitializing) {
      return;
    }
    const currentProviderId = selectedProvider?.id || null;
    const previousProviderId = formStateRef.current.previousSelectedProviderId;
    if (shouldClearCredentials(currentProviderId, previousProviderId)) {
      const providerCapabilities = getProviderCapabilities(selectedProvider);

      clearErrors();
      clearCredentialsFields();
      resetCredentialsObject(selectedProvider);
      setValue("brands", [], { shouldValidate: false });
      if (!isMerchantIdConstInSchema(selectedProvider?.schema)) {
        setValue("merchantId", "", { shouldValidate: false });
      }

      if (providerCapabilities.isPriorityEditable) {
        setValue("priority", undefined, { shouldValidate: false });
      } else {
        setValue("priority", providerCapabilities.priorityFixedValue, {
          shouldValidate: false,
        });
      }

      applySchemaConstValuesToForm(
        selectedProvider?.schema,
        setValue,
        getValues,
      );
    }
    formStateRef.current.previousSelectedProviderId = currentProviderId;
  }, [
    selectedProvider?.id,
    selectedProvider,
    shouldClearCredentials,
    clearCredentialsFields,
    resetCredentialsObject,
    formStateRef,
    clearErrors,
    setValue,
    getValues,
  ]);
}
